import React, { useState } from "react";
import { useNotifications } from "../../contexts/NotificationContext";
import { formatDistanceToNow } from "date-fns";
import { Bell, CheckCircle, AlertCircle, RefreshCw } from "lucide-react";
import { Link } from "react-router-dom";
import { ROUTES } from "../../lib/routes";

export function NotificationCenter() {
  const {
    notifications,
    loading,
    error,
    markAsRead,
    markAllAsRead,
    fetchNotifications,
  } = useNotifications();
  const [filter, setFilter] = useState<"all" | "unread">("all");

  const filteredNotifications =
    filter === "all"
      ? notifications
      : notifications.filter((notification) => !notification.read);

  const getNotificationIcon = (type: string) => {
    switch (type) {
      case "integration_request":
        return <Bell className="h-5 w-5 text-blue-500" />;
      case "integration_accepted":
        return <CheckCircle className="h-5 w-5 text-green-500" />;
      case "integration_rejected":
        return <AlertCircle className="h-5 w-5 text-red-500" />;
      default:
        return <Bell className="h-5 w-5 text-gray-500" />;
    }
  };

  return (
    <div className="extranet-container">
      <div className="extranet-container-header">
        <div className="flex items-center justify-between">
          <div className="flex items-center">
            <Bell className="h-6 w-6 text-indigo-600 mr-2" />
            <h2 className="text-xl font-bold text-gray-900">Notifications</h2>
          </div>
          <div className="flex space-x-2">
            <button
              onClick={() => setFilter("all")}
              className={`px-3 py-1 text-sm rounded-md ${
                filter === "all"
                  ? "bg-indigo-100 text-indigo-700"
                  : "bg-white text-gray-600 hover:bg-gray-100"
              }`}
            >
              All
            </button>
            <button
              onClick={() => setFilter("unread")}
              className={`px-3 py-1 text-sm rounded-md ${
                filter === "unread"
                  ? "bg-indigo-100 text-indigo-700"
                  : "bg-white text-gray-600 hover:bg-gray-100"
              }`}
            >
              Unread
            </button>
            <button
              onClick={() => fetchNotifications()}
              className="p-1 rounded-md text-gray-600 hover:bg-gray-100"
              title="Refresh notifications"
            >
              <RefreshCw className="h-5 w-5" />
            </button>
            {filteredNotifications.some((n) => !n.read) && (
              <button
                onClick={() => markAllAsRead()}
                className="px-3 py-1 text-sm bg-white text-indigo-600 hover:text-indigo-800 rounded-md"
              >
                Mark all as read
              </button>
            )}
          </div>
        </div>
      </div>

      <div className="extranet-container-body">
        {loading ? (
          <div className="py-6 text-center text-sm text-gray-500">
            Loading notifications...
          </div>
        ) : error ? (
          <div className="py-6 text-center text-sm text-red-500">{error}</div>
        ) : filteredNotifications.length === 0 ? (
          <div className="py-12 text-center">
            <Bell className="mx-auto h-12 w-12 text-gray-400" />
            <h3 className="mt-2 text-sm font-medium text-gray-900">
              No notifications
            </h3>
            <p className="mt-1 text-sm text-gray-500">
              {filter === "unread"
                ? "You have no unread notifications."
                : "You don't have any notifications yet."}
            </p>
          </div>
        ) : (
          <div className="divide-y divide-gray-200">
            {filteredNotifications.map((notification) => (
              <div
                key={notification.id}
                className={`p-4 hover:bg-gray-50 ${
                  !notification.read ? "bg-blue-50" : ""
                }`}
              >
                <div className="flex items-start">
                  <div className="flex-shrink-0 pt-0.5">
                    {getNotificationIcon(notification.type)}
                  </div>
                  <div className="ml-3 flex-1">
                    <div className="flex items-center justify-between">
                      <p className="text-sm font-medium text-gray-900">
                        {notification.title}
                      </p>
                      <p className="text-xs text-gray-500">
                        {formatDistanceToNow(
                          new Date(notification.created_at),
                          { addSuffix: true }
                        )}
                      </p>
                    </div>
                    <p className="text-sm text-gray-600 mt-1">
                      {notification.message}
                    </p>
                    <div className="mt-2 flex">
                      {!notification.read && (
                        <button
                          onClick={() => markAsRead(notification.id)}
                          className="text-xs text-indigo-600 hover:text-indigo-800"
                        >
                          Mark as read
                        </button>
                      )}
                      {notification.type === "integration_request" &&
                        !notification.read && (
                          <Link
                            to={ROUTES.hotelExtranet("integrations", {
                              view: "pending",
                            })}
                            className="ml-4 text-xs text-indigo-600 hover:text-indigo-800"
                          >
                            View request
                          </Link>
                        )}
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
